<template>
  <v-container id="document_signature_customization_root" fluid>
    <implicit-authorization-modal
      :key="implicitAuthorizationModalKey"
      :signature-guid="implicitGuid"
      :request-loading-dialog-showed="ImplicitAuthorizationModalVisible"
    ></implicit-authorization-modal>
    <!-- header -->
    <v-row class="mb-6 px-6" justify="center" align="center">
      <v-btn
        id="back_button"
        class="elevation-0 pl-0 pr-3"
        color="black--text grey lighten-2"
        @click="back"
      >
        <v-icon class="ma-0 pa-0" color="grey darken-3"> mdi-chevron-left </v-icon>
        {{ $t("main.back") }}
      </v-btn>
      <v-col cols="7">
        <span id="filename" class="text-h5"> {{ document.fileName }}</span>
      </v-col>

      <v-col class="d-flex flex-row justify-end">
        <v-btn
          v-if="!useExternalSignature"
          id="signature_confirmation_sign_btn"
          class="mx-4 px-4"
          color="success"
          :disabled="isSignButtonDisabled"
          :loading="isUploading"
          @click="handleSignClick"
        >
          <v-icon class="mr-1 ml-0">mdi-draw-pen</v-icon>
          {{ $t("main.sign") }}
        </v-btn>
        <v-btn
          v-if="useExternalSignature"
          id="signature_confirmation_external_sign_btn"
          class="mx-4 px-4"
          color="success"
          :disabled="
            !uploadedExternalFileName ||
              !uploadedExternalFile ||
              !externalSignatureIdsCorrect
          "
          :loading="isUploading"
          @click="uploadExternalSignedDocument()"
        >
          <v-icon class="mr-1 ml-0">mdi-draw-pen</v-icon>
          {{
            $t(
              "documentSignatureCustomization.content.signature.externalSignature.submit"
            )
          }}
        </v-btn>

        <v-btn
          id="decline_btn"
          color="error"
          outlined
          class="px-2"
          :loading="isUploading"
          @click="showDeclineModal($event)"
        >
          <v-icon class="mr-1 ml-0"> mdi-close-circle-outline </v-icon>
          {{ $t("dataTable.content.action.decline") }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row align="stretch" align-content="space-between">
      <v-col md="6" sm="12" align="start">
        <!-- SIGNATURE + CRED  CONFIG-->
        <v-card v-show="pdfRendered">
          <v-container class="d-flex flex-column">
            <v-form ref="form" v-model="formValid" @input="handleFormValidityChange">
              <v-row v-show="pdfRendered">
                <v-col v-if="useExternalSignatureVisible">
                  <v-switch
                    id="use_external_signature"
                    v-model="useExternalSignature"
                    :label="
                      $t(
                        'documentSignatureCustomization.content.signature.useExternalSignature'
                      )
                    "
                    class="mx-3"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row v-show="pdfRendered && !useExternalSignature">
                <v-col>
                  <v-switch
                    id="set_visible_signature"
                    v-model="useVisibleSignature"
                    :label="
                      $t(
                        'documentSignatureCustomization.content.signature.setVisibleSignature'
                      )
                    "
                    :disabled="
                      !!predefinedSignatureParams ||
                        (sealActivated && credentialsWithCentralKeyToo.length == 1)
                    "
                    class="mx-3"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row v-if="!useExternalSignature && useVisibleSignature" class="mb-4">
                <v-btn
                  v-if="drawYourSignatureVisible"
                  id="draw_signature"
                  class="ml-3 pa-1 pr-2 body-2 white--text modalButton"
                  @click="useCustomSignatureClick"
                >
                  <v-icon>mdi-pen</v-icon>
                  {{ $t("signatureDrawer.title") }}
                </v-btn>

                <v-btn
                  v-if="useCustomDrawnSignatureImage"
                  id="clear_drawed_signature"
                  class="ml-3 pa-1 pr-2 body-2 white--text modalButton"
                  @click="clearCutomSignatureImageClick"
                >
                  <v-icon>mdi-refresh</v-icon>
                  {{
                    $t(
                      "documentSignatureCustomization.content.signature.clearCutomSignatureImage"
                    )
                  }}
                </v-btn>
              </v-row>
              <v-row v-show="!useExternalSignature && useSignatureProfile">
                <v-col>
                  <v-divider role="presentation"></v-divider>
                  <v-select
                    id="select_signature_profile"
                    ref="selectedSignatureProfile"
                    v-model="selectedSignatureProfile"
                    class="mx-3 mt-5 mb-4"
                    :items="signatureProfiles"
                    item-text="name"
                    item-value="id"
                    return-object
                    :label="
                      $t(
                        'documentSignatureCustomization.content.signature.selectSignatureProfile'
                      ) + `*`
                    "
                    :rules="
                      !useSignatureProfile ? [] : [rules.required, rules.credentialAlive]
                    "
                    :error-messages="useSignatureProfile ? (credentialAliveError ? [credentialAliveError] : []) : []"
                    :disabled="!useSignatureProfile"
                  ></v-select>

                  <p v-show="showBlueWarning" class="BlueWarning">
                    {{
                      $t(
                        "documentSignatureCustomization.warning.someCredendetialHaveProblem"
                      )
                    }}
                  </p>

                  <v-row align="center" class="mx-3 mb-3">
                    <span>{{
                      $t(
                        "documentSignatureCustomization.content.signature.saveSignaturePosInProfile"
                      )
                    }}</span>
                    <v-btn
                      id="save_position_btn"
                      class="mx-3 pa-1"
                      color="success"
                      outlined
                      :disabled="!checkUniquePosition()"
                      @click="saveImagePosToProfile()"
                    >
                      <v-icon class="mr-1 ml-0">{{ saveImagePositionIcon }}</v-icon>
                      {{ $t("main.save") }}
                    </v-btn>
                  </v-row>
                  <v-switch
                    v-show="credentialsWithCentralKeyToo.length > 0"
                    v-model="useDifferentCredential"
                    :label="
                      $t(
                        'documentSignatureCustomization.content.signature.useDifferentCredential'
                      )
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row v-if="!useExternalSignature">
                <v-col v-show="showCredentialSelector">
                  <v-divider role="presentation"></v-divider>
                  <v-select
                    v-if="credentialsWithCentralKeyToo.length > 0"
                    id="signature_customization_credential_select"
                    v-model="selectedCredential"
                    class="mx-3 mt-5"
                    :items="credentialsWithCentralKeyTooComputed"
                    :item-text="getCredentialItemText"
                    :return-object="true"
                    :label="
                      $t(
                        'documentSignatureCustomization.content.signature.selectSigningCredential'
                      ) + `*`
                    "
                    :rules="showCredentialSelector ? [rules.requiredSimple] : []"
                  >
                    <!-- <template v-slot.item="{item}">
                      <div class="option_active_credential">
                        {{ item }}
                      </div>
                    </template> -->
                  </v-select>
                  <p v-show="showBlueWarning" class="BlueWarning">
                    {{
                      $t(
                        "documentSignatureCustomization.warning.someCredendetialHaveProblem"
                      )
                    }}
                  </p>
                </v-col>
              </v-row>
              <v-row v-if="useExternalSignature">
                <v-col>
                  <v-btn
                    id="download-document-external-signature"
                    class="mx-4 px-4"
                    @click="downloadForExternalSign()"
                  >
                    <v-icon class="mr-1 ml-0">mdi-download</v-icon>
                    {{
                      $t(
                        "documentSignatureCustomization.content.signature.externalSignature.download"
                      )
                    }}
                  </v-btn>
                </v-col>
              </v-row>
              <v-row v-if="useExternalSignature">
                <v-col>
                  <drag-drop-upload
                    id="upload_file"
                    :key="dragDropKey"
                    given-height="20vh"
                    max-width="auto"
                    :valid-formats="['application/pdf']"
                    :current-file="uploadedExternalFile"
                    :single-file="true"
                    :upload-caption="
                      $t(
                        'documentSignatureCustomization.content.signature.externalSignature.upload'
                      )
                    "
                    :description="$t('dragDrop.uploadFiles')"
                    @uploaded="handleExternalSignatureUploaded"
                  >
                    <v-progress-circular
                      v-if="isUploading"
                      slot="progress-bar"
                      indeterminate
                      color="primary"
                      class="mb-4"
                    ></v-progress-circular>
                  </drag-drop-upload>
                </v-col>
              </v-row>
            </v-form>

            <v-row class="pa-3">
              <v-text-field
                id="comment_text_field"
                v-model="comment"
                prepend-icon="mdi-comment modalButtonColor--text large"
                persistent-hint
              >
                <template #label>
                  <span class="text--secondary">{{
                    $t("documentSignatureCustomization.comments.label")
                  }}</span>
                </template>
              </v-text-field>
            </v-row>
          </v-container>
        </v-card>
      </v-col>
      <!-- visibleSignatureComponent -->
      <v-col md="6" sm="12" align="center" style="position: relative">
        <v-overlay :absolute="true" :value="isUploading">
          <v-progress-circular
            indeterminate
            size="64"
            color="primary"
          ></v-progress-circular>
        </v-overlay>
        <visibleSignature
          ref="visibleSignature"
          :doc-data="documentBase64"
          :sign-prof="selectedSignatureProfile"
          :pdf-encrypted.sync="pdfEncrypted"
          :allow-sign-profile-editing="!predefinedSignatureParams"
          @updatedSignProf="updatedSignatureProfile"
          @updatedTextSize="updatedTextSize"
          @updatedSignatureProfileImage="updatedSignatureProfileImage"
          @pdfLoaded="pdfLoaded"
          @passwordSpecified="onPDFPasswordSpecified"
          @passwordCancelled="handlePasswordCancelled"
        />
      </v-col>
    </v-row>
    <!-- authorizationModal -->
    <v-row justify="center">
      <v-dialog v-model="authorizationModal" persistent max-width="500">
        <v-card id="explicit_auth_modal">
          <v-card-title>
            <span class="headline">{{
              $t("documentSignatureCustomization.modal.authorization.title")
            }}</span>
          </v-card-title>
          <v-card-text>
            <v-form
              ref="authorizationModalForm"
              v-model="authorizationModalFormValid"
              @submit.prevent
            >
              <v-text-field
                id="explicit_authmodal_textfield"
                ref="passwordField"
                v-model="expAuthPwd.pwd"
                tabindex="1"
                :append-icon="expAuthPwd.showPwd ? 'mdi-eye' : 'mdi-eye-off'"
                :label="
                  $t(
                    'documentSignatureCustomization.modal.authorization.content.keyPassword'
                  ) + `*`
                "
                :type="expAuthPwd.showPwd ? 'text' : 'password'"
                prepend-icon="mdi-lock"
                :rules="[rules.required, rules.passwordMustBeLongerThan]"
                @click:append="expAuthPwd.showPwd = !expAuthPwd.showPwd"
                @keydown.enter="signDoc()"
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn text tabindex="3" @click="authorizationModal = false">
              {{
                $t("main.cancel")
              }}
            </v-btn>
            <v-btn
              id="explicit_authmodal_signbtn"
              color="success"
              tabindex="2"
              :disabled="!authorizationModalFormValid"
              :loading="authorizationModalBtnLoading"
              @click="signDoc()"
            >
              {{
                $t(
                  "documentSignatureCustomization.modal.authorization.content.button.sign"
                )
              }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>

    <v-row justify="center">
      <v-dialog
        v-model="noCredentialWarningVisible"
        persistent
        width="auto"
        max-width="700"
      >
        <v-card id="no_credential_warning" class="py-6 px-3">
          <v-card-title class="text-h6 red--text">
            {{ $t("signatureProfiles.error.noActivatedCredential.title") }}
          </v-card-title>
          <v-card-text>
            <div>
              {{ $t("signatureProfiles.error.noActivatedCredential.description") }}
            </div>
            <v-btn
              id="no_credential_warning_ok"
              style="text-align: center"
              color="primary"
              to="/credentials"
              class="mt-10 mb-0"
            >
              <v-icon class="mr-1">mdi-folder-open-outline</v-icon>
              {{ $t("signatureProfiles.error.noActivatedCredential.button") }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>

    <v-row justify="center">
      <v-dialog
        v-model="notVerifiedUserWarningVisible"
        persistent
        width="auto"
        max-width="700"
      >
        <v-card id="not_verifiedUser_warning" class="py-6 px-3">
          <v-card-title class="text-h6 red--text">
            {{ $t("signatureProfiles.error.notVerifiedUser.title") }}
          </v-card-title>
          <v-card-text>
            <div>
              {{ $t("signatureProfiles.error.notVerifiedUser.description") }}
            </div>
            <v-btn
              id="not_verified_user_warning_ok"
              style="text-align: center"
              color="primary"
              class="mt-10 mb-0"
              to="/user/profile"
            >
              <v-icon class="mr-1">mdi-folder-open-outline</v-icon>
              {{ $t("signatureProfiles.error.notVerifiedUser.button") }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>

    <v-row justify="center">
      <v-dialog
        v-model="noDeviceForImplicitCredentialWarningVisible"
        persistent
        max-width="550"
      >
        <v-card id="no_device_warning" class="pa-6">
          <v-card-title class="text-h6 red--text">
            {{ $t("signatureProfiles.error.noActivatedDevice.title") }}
          </v-card-title>

          <v-card-text>
            <v-container>
              <v-row justify="start">
                <div>
                  {{ $t("signatureProfiles.error.noActivatedDevice.description") }}
                </div>
                <div class="pt-6">
                  <v-btn
                    id="no_device_warning_ok"
                    color="primary"
                    to="/user/profile/"
                    class="mb-0"
                  >
                    <v-icon class="mr-1">mdi-cellphone-key</v-icon>
                    {{ $t("signatureProfiles.error.noActivatedDevice.button") }}
                  </v-btn>
                </div>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>

    <v-row justify="center">
      <v-dialog
        v-model="centralKeyEnabledAndnoProfileWithCentralKeyWarningVisible"
        persistent
        width="auto"
        max-width="700"
      >
        <v-card
          id="centralKey_Enabled_And_noProfile_WithCentralKey_warning"
          class="py-6 px-3"
        >
          <v-card-title class="text-h6 red--text">
            {{
              $t(
                "signatureProfiles.error.centralKeyEnabledAndNoProfileWithCentralKey.title"
              )
            }}
          </v-card-title>
          <v-card-text>
            <div>
              {{
                $t(
                  "signatureProfiles.error.centralKeyEnabledAndNoProfileWithCentralKey.description"
                )
              }}
            </div>
            <v-btn
              id="centralKey_Enabled_And_noProfile_WithCentralKey_warning_ok"
              style="text-align: center"
              color="primary"
              to="/signatureProfiles"
              class="mt-10 mb-0"
            >
              <v-icon class="mr-1">mdi-folder-open-outline</v-icon>
              {{
                $t(
                  "signatureProfiles.error.centralKeyEnabledAndNoProfileWithCentralKey.button"
                )
              }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>

    <v-row justify="center">
      <v-dialog
        v-model="centralKeyEnabledAndnoProfileWithCentralKeyWarningVisible"
        persistent
        width="auto"
        max-width="700"
      >
        <v-card
          id="centralKey_Enabled_And_noProfile_WithCentralKey_warning"
          class="py-6 px-3"
        >
          <v-card-title class="text-h6 red--text">
            {{
              $t(
                "signatureProfiles.error.centralKeyEnabledAndNoProfileWithCentralKey.title"
              )
            }}
          </v-card-title>
          <v-card-text>
            <div>
              {{
                $t(
                  "signatureProfiles.error.centralKeyEnabledAndNoProfileWithCentralKey.description"
                )
              }}
            </div>
            <v-btn
              id="centralKey_Enabled_And_noProfile_WithCentralKey_warning_ok"
              style="text-align: center"
              color="primary"
              to="/signatureProfiles"
              class="mt-10 mb-0"
            >
              <v-icon class="mr-1">mdi-folder-open-outline</v-icon>
              {{
                $t(
                  "signatureProfiles.error.centralKeyEnabledAndNoProfileWithCentralKey.button"
                )
              }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>

    <v-row>
      <decline-reason-modal
        :visible="declineReasonVisible"
        :signature-id="+$route.params.signatureId"
        :current-signature-id="$route.params.currentSignatureId"
        @declined="back"
        @closed="declineReasonVisible = false"
      ></decline-reason-modal>
    </v-row>
    <v-row>
      <signature-drawer
        ref="signatureDrawer"
        v-model="showSignatureModal"
        @saved="handleCustomSignature"
        @cancel="handleCustomSignatureCancelled"
      ></signature-drawer>
    </v-row>
    <!-- AlfaTrust PIN + OTP dialog -->
    <v-row justify="center">
      <sign-alfasign ref="signAlfasign" @done="onAlfaDone" @cancel="onAlfaCancel" />
    </v-row>
  </v-container>
</template>

<script>
import SignAlfasign from '../../components/alfasign/SignAlfasign.vue'

import DragDropUpload from '@/components/DragDropUpload.vue'
import SignatureDrawer from '@/components/SignatureDrawer.vue'
import credentialRefs from '@/services/api/credentialRefs'
import csc from '@/services/api/csc'
import evro from '@/services/api/evro'
import rssp from '@/services/api/rsspUtil.js'
import seal from '@/services/api/seal.js'
import signatureChains from '@/services/api/signatureChains'
import signatureChainType from '@/views/signature/chains/SignatureChainType'
import documentDownloadMode from '@/views/signature/chains/documentDownloadMode.js'
import memberRole from '@/views/signature/chains/memberRole'
import FileSaver from 'file-saver'
import { UserManager } from 'oidc-client-ts'
import { v4 as uuidv4 } from 'uuid'
import VisibleSignature from '../../components/VisibleSignature'
import {
  showConfirmationMenu,
  showRequestLoadingDialog,
  showToastError,
  showToastSuccess
} from '../../event-bus'
import documents from '../../services/api/documents'
import signatureProfiles from '../../services/api/signatureProfiles'
import signatures from '../../services/api/signatures'
import userVerification from '../../services/api/userVerification'
import users from '../../services/api/users'
import CommonError from '../CommonError'
import CredentialAuthMode from './CredentialAuthMode'
import DeclineReasonModal from './DeclineReasonModal'
import ImplicitAuthorizationModal from './ImplicitAuthorizationModal'

import alfaRefs from '@/services/api/alfaRefs'

export default {
  name: 'DocumentSignatureCustomization',
  components: {
    SignatureDrawer,
    VisibleSignature,
    ImplicitAuthorizationModal,
    DeclineReasonModal,
    DragDropUpload,
    SignAlfasign
  },

  data() {
    return {
      providers: [],
      formValid: true,
      evroEnabled: this.$store.state.remoteConfig.evroEnabled
        ? this.$store.state.remoteConfig.evroEnabled
        : false,
      documentBase64: null,
      document: {},
      pdfRendered: false,
      useVisibleSignature: true, // from v1.11 we set this to default true
      useSignatureProfile: signatureProfiles.length > 0,
      useDifferentCredential: false,
      useCustomDrawnSignatureImage: false,
      userDrawedSignatureImage: null,
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      },
      rsspRemoteConf: null,
      externalSignaturesEnabled: this.$store.state.remoteConfig.externalSignaturesEnabled
        ? this.$store.state.remoteConfig.externalSignaturesEnabled
        : false,
      useExternalSignature: false,
      externalSignatureIdsCorrect: false,
      chain: null,
      dragDropKey: 0,
      signatureProfiles: [],
      selectedSignatureProfile: {
        credentialId: ''
      },
      signatureImg: null,
      signatureTextSize: 14,
      pdfEncrypted: false,
      pdf_password: null,
      activatedCredentials: [],
      credentialRefs: [],
      credentialsEvro: [],
      credentialsAlfa: [],
      myCredentials: [],
      idToken: null,
      credentialsWithCentralKeyToo: [],
      selectedCredential: null,
      switchSignatureChainType: false,
      draftRecipients: [{ userId: null }],
      authorizationModalFormValid: false,
      authorizationModal: false,
      authorizationModalBtnLoading: false,
      // implicitAuthorizationModal
      implicitAuthorizationModalKey: 0,
      implicitGuid: '',
      devicesListTotalItems: 0,
      devicesActiveTotalItems: 0,
      ImplicitAuthorizationModalVisible: false,
      showBlueWarning: false,
      noCredentialWarningVisible: false,
      noDeviceForCredentialWarningVisible: false,
      noDeviceForImplicitCredentialWarningVisible: false,
      notVerifiedUserWarningVisible: false,
      centralKeyEnabledAndnoProfileWithCentralKeyWarningVisible: false,
      user: null,
      authorizationTimeoutMilis: this.$store.state.remoteConfig
        ? this.$store.state.remoteConfig.authorizationTimeoutSecs * 1000
        : null,
      evroTrustAuthorizationTimeoutMilis: this.$store.state.remoteConfig
        ? this.$store.state.remoteConfig.evroAuthorizationTimeoutSecs * 1000
        : 60 * 1000,
      showSignatureModal: false,
      centralKeyId: -1,
      originalSignatureProfiles: [],
      userVerificationStatus: {
        verified: true,
        fields: [
          {
            id: 'SURNAME',
            displayName: 'vezetéknév',
            optional: true,
            value: 'Surname value'
          },
          { id: 'GIVEN_NAMES', displayName: 'keresztnév', optional: false },
          {
            id: 'NATIONALITY',
            displayName: 'nemzetiség',
            optional: false,
            value: 'Magyar'
          }
        ]
      },
      expAuthPwd: {
        pwd: null,
        showPwd: false
      },
      uploadedExternalFile: null,
      uploadedExternalFileName: null,
      credentialAliveError: null,
      passwordMinCharacters: 6,
      rules: {
        requiredSimple: (v) => !!v || this.$t('validation.form.requiredField'),
        required: (v) =>
          !!v || !isNaN(parseFloat(v)) || this.$t('validation.form.requiredField'),
        passwordMustBeLongerThan: (v) =>
          (v && v.length >= this.passwordMinCharacters) ||
          this.$t('validation.form.passwordMustBeLongerThan', {
            min: this.passwordMinCharacters
          }),
        onlyNumber: (v) => /^[0-9]+$/.test(v) || this.$t('validation.form.onlyNumbers'),
        credentialAlive: (v) => {
          // console.log('credentialAlive called')
          this.credentialAliveError = ''

          if (!this.useSignatureProfile) return true

          const isEvroCred = !!(
            this.selectedCredential && this.selectedCredential.isEvro
          )
          const isAlfaCred =
            this.isAlfaTrust ||
            (!!this.selectedSignatureProfile?.alfaRefId && !this.useDifferentCredential)

          const isCredRef = !!(
            this.selectedCredential && this.selectedCredential.provider
          )

          const searchingForCredId = !this.useDifferentCredential
            ? Number(this.selectedSignatureProfile?.credentialId ?? NaN)
            : Number(this.selectedCredential?.id ?? NaN)

          const foundCred =
            Number.isFinite(searchingForCredId) &&
            this.activatedCredentials.some((c) => Number(c.id) === searchingForCredId)

          const existActvatedCred = !!this.selectedCredential && foundCred

          if (this.sealActivated) {
            if (
              this.useSignatureProfile &&
              !this.useDifferentCredential &&
              this.selectedSignatureProfile.useSeal
            ) {
              const res =
                this.sealActivated || this.$t('validation.form.centralKeyIsNotActivated')
              this.credentialAliveError = typeof res === 'boolean' ? '' : res
              return res
            }

            if (this.useSignatureProfile) {
              const ok =
                (!this.useDifferentCredential && existActvatedCred) ||
                this.useDifferentCredential ||
                isCredRef ||
                isEvroCred ||
                isAlfaCred
              const res = ok || this.$t('validation.form.credentialIsDead')
              this.credentialAliveError = typeof res === 'boolean' ? '' : res
              return res
            }

            return true
          }

          const ok =
            (!this.useDifferentCredential && existActvatedCred) ||
            this.useDifferentCredential ||
            isCredRef ||
            isEvroCred ||
            isAlfaCred
          const res = ok || this.$t('validation.form.credentialIsDead')
          // console.log('credentialAlive res', res)
          this.credentialAliveError = typeof res === 'boolean' ? '' : res
          return res
        },
        noAlfaWithDetached: (v) =>
          (this.chain.signingTrailMode === 'DETACHED' &&
            !!v.alfaRefId &&
            !this.useDifferentCredential) ||
          'credentials.alfa.detachedError'
      },
      signAlfasign: null,

      isUploading: true,

      declineReasonVisible: false,
      saveImagePositionIcon: 'mdi-content-save-outline',
      popupLoginWidth: this.$store.state.remoteConfig.popupLogin?.width ?? 770,
      popupLoginHeight: this.$store.state.remoteConfig.popupLogin?.height ?? 720,
      comment: ''
    }
  },

  computed: {
    useExternalSignatureVisible() {
      return (
        this.externalSignaturesEnabled &&
        this.chain &&
        this.chain.signingTrailMode !== 'DETACHED'
      )
    },
    drawYourSignatureVisible() {
      return !this.useCustomDrawnSignatureImage
    },
    showCredentialSelector() {
      return (
        !this.useVisibleSignature ||
        (this.useVisibleSignature && !this.useSignatureProfile) ||
        (this.useVisibleSignature &&
          this.useSignatureProfile &&
          this.useDifferentCredential)
      )
    },
    predefinedSignatureParams() {
      const { originX, originY, width, height, page } = this.$route.query
      if ([originX, originY, width, height, page].every((v) => v === undefined)) {
        return null
      }
      const parsed = {
        originX: parseFloat(originX),
        originY: parseFloat(originY),
        width: parseFloat(width),
        height: parseFloat(height),
        page: parseInt(page)
      }

      const isValid =
        !isNaN(parsed.originX) &&
        !isNaN(parsed.originY) &&
        !isNaN(parsed.width) &&
        !isNaN(parsed.height) &&
        !isNaN(parsed.page)

      return isValid ? parsed : null
    },
    currentUser() {
      return this.$store.state.currentUser
    },

    // from validating not working correctly on this page, so made this not so nice hack. todo: fix the validation issues
    isFormValid() {
      // console.log('isFormValid called')
      const isSigner = this.hasRoleInternal(memberRole.SIGNER.toLowerCase())
      if (!isSigner) {
        return false
      }

      if (
        this.sealActivated &&
        !this.useSignatureProfile &&
        (!this.selectedCredential || !this.selectedCredential.id)
      ) {
        return false
      }
      if (
        !this.useSignatureProfile &&
        this.useVisibleSignature &&
        !this.userDrawedSignatureImage
      ) {
        return false
      }

      const selectedCredIsImplicit =
        this.selectedCredential && this.isImplicitCredential
      const implicitValid =
        !selectedCredIsImplicit ||
        (selectedCredIsImplicit && this.devicesActiveTotalItems > 0)

      const formValidRes = this.formValid
      // console.log('isFormValid formValidRes', formValidRes)
      // console.log('isFormValid useSignatureProfile', this.useSignatureProfile)

      const res = formValidRes && implicitValid
      // console.log('isFormValid res', res)

      return res
    },

    sealActivated() {
      return this.sealStatus.enabled && this.sealStatus.activated
    },

    sealEnabled() {
      return this.sealStatus.enabled
    },

    isVerificationEnabled() {
      return !!this.$store.state.remoteConfig.regulaEnabled
    },
    credentialsWithCentralKeyTooComputed() {
      return this.credentialsWithCentralKeyToo.map((item, index) => {
        if (this.sealActivated) {
          const centralKeyDisabled =
            (this.sealActivated && !this.useVisibleSignature) ||
            (this.useDifferentCredential &&
              this.useSignatureProfile &&
              this.selectedSignatureProfile &&
              this.selectedSignatureProfile.useSeal)

          if (index === 0 && centralKeyDisabled) {
            return {
              ...item,
              disabled: true
            }
          }
          return item
        } else {
          return item
        }
      })
    },
    isExplicitCredential() {
      return this.selectedCredential && (this.selectedCredential.authMode === CredentialAuthMode.EXPLICIT)
    },

    isImplicitCredential() {
      // console.log('isImplicitCredential computed called')
      return this.selectedCredential && (this.selectedCredential.authMode === CredentialAuthMode.IMPLICIT)
    },

    isOAUTH2CODECredential() {
      return this.selectedCredential && (this.selectedCredential.authMode === 'OAUTH2CODE')
    },

    isCredentialUnknownAuthMode() {
      return this.selectedCredential && (!this.selectedCredential.authMode)
    },
    isAlfaTrust() {
      return this.selectedCredential && !!(this.selectedCredential.isAlfa)
    },
    isSealSignMode() {
      const resp = !!(this.sealActivated && ((this.activatedCredentials.length === 0 && this.credentialRefs.length === 0 && this.credentialsEvro.length === 0) ||
        (this.useSignatureProfile && this.selectedSignatureProfile && this.selectedSignatureProfile.useSeal && !this.useDifferentCredential) ||
        (this.selectedCredential && this.selectedCredential.id && this.selectedCredential.id === this.centralKeyId)))
      return resp
    },
    isSignButtonDisabled() {
      // console.log('isSignButtonDisabled', !this.isFormValid)
      return !this.isFormValid
    }
  },

  watch: {
    async selectedSignatureProfile(val, oldVal) {
      this.selectedSignatureProfileImplementation(val, oldVal)
    },
    async selectedCredential(val) {
      if (val && val.provider) {
        // const credInfo = await this.getCredentialInfosWithCSC()
      }
      if (this.selectedCredential && this.selectedCredential.id === undefined) {
        this.selectedCredential = null
      }
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.checkFormValidity()
        }
      })
    },

    async useDifferentCredential(val) {
      if (!val) {
        if (!this.useSignatureProfile) {
          this.selectedCredential = {}
        } else if (this.selectedSignatureProfile) {
          if (this.selectedSignatureProfile.credentialRefId) {
            this.selectedCredential = this.findCredentialByRefId(
              parseInt(this.selectedSignatureProfile.credentialRefId)
            )
            const credInfo = this.getCredentialInfosWithCSC()

            // console.log('credref credentialsInfo', credInfo)
          } else if (this.selectedSignatureProfile.evroRefId) {
            this.selectedCredential = this.findCredentialByEvroRefId(
              parseInt(this.selectedSignatureProfile.evroRefId)
            )
          } else {
            this.selectedCredential = this.findCredentialById(
              parseInt(this.selectedSignatureProfile.credentialId)
            )
          }
        }
        if (
          this.sealActivated &&
          this.selectedSignatureProfile &&
          this.selectedSignatureProfile.useSeal
        ) {
          // this.selectedCredential.id = this.centralKeyId
          this.selectedCredential = this.findCredentialById(-1)
        }
      } else {
        this.selectedCredential = undefined
      }

      // this.$refs.form.resetValidation()
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.checkFormValidity()
        }
      })
    },

    async useSignatureProfile(val) {
      if (val) {
        // this.selectedCredential = {}
      } else {
        this.selectedSignatureProfile = {}
      }

      this.$refs.form.resetValidation()
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.checkFormValidity()
        }
      })
    },
    async useVisibleSignature(val) {
      if (val) {
        if (this.signatureProfiles.length > 0) {
          this.useSignatureProfile = true
          this.selectedSignatureProfile = this.signatureProfiles[0]
        }
        this.useSignatureProfile =
          !this.useCustomDrawnSignatureImage &&
          !this.useSignatureProfile &&
          this.signatureProfiles.length > 0
        this.useCustomDrawnSignatureImage = false
        this.userDrawedSignatureImage = null
        this.selectFirstProfile()
        this.selectFirstCredential()
      } else {
        this.useSignatureProfile = false
        this.selectedSignatureProfile = null
        this.useCustomDrawnSignatureImage = false
        this.userDrawedSignatureImage = null
      }

      this.useDifferentCredential = false
      if (!val) {
        this.useSignatureProfile = false
      } else {
        // this.selectFirstProfile()
      }
      this.$refs.form.resetValidation()
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.checkFormValidity()
        }
      })
    }
  },

  async mounted() {
    await this.getSignatureProfiles()
    await this.downloadDocument()

    this.providers = await credentialRefs.getExternalProviders('web')

    // too early: this.selectFirstProfile()

    this.sealStatus = await seal.getSealStatus()
    // console.log('sealStatus', this.sealStatus)

    this.user = await users.getMyUser()

    try {
      await this.listMyActivatedCredentials()
      // console.log('this.activatedCredentials', this.activatedCredentials)

      await this.listImportedCredentials()
      // console.log('credRefs', this.credentialRefs)

      // this.useVisibleSignature = !!this.predefinedSignatureParams

      this.credentialsWithCentralKeyToo = [
        ...this.activatedCredentials,
        ...this.credentialRefs,
        ...this.credentialsEvro,
        ...this.credentialsAlfa
      ]
      // console.log('this.sealActivated', this.sealActivated)
      // console.log('this.credentialsWithCentralKeyToo', this.credentialsWithCentralKeyToo)
      if (this.sealActivated) {
        this.credentialsWithCentralKeyToo.unshift({
          id: -1,
          description: this.$t(
            'documentSignatureCustomization.content.signature.centralKey'
          )
        })
        /* if (this.credentialsWithCentralKeyToo.length === 1) {
         this.useVisibleSignature = true
       } */
      }

      this.selectFirstProfile()

      // this.user.verified = true // todo remove in production
      this.notVerifiedUserWarningVisible =
        this.isVerificationEnabled &&
        this.user &&
        this.user.verificationMandatory &&
        !this.user.verified
      if (this.isVerificationEnabled) {
        await this.getVerificationDetails()
      }
    } catch (error) {
      console.error(error)
    }

    if (
      this.sealActivated &&
      !this.useSignatureProfile &&
      this.signatureProfiles.length === 0 &&
      this.credentialsWithCentralKeyToo.length === 1
    ) {
      this.$nextTick(() => {
        this.selectedCredential = this.findCredentialById(-1)
      })
    } /* else {
      this.selectFirstProfile()
      this.selectFirstCredential()
    } */

    this.$nextTick(() => {
      if (this.$refs.form) {
        this.checkFormValidity()
      }
    })

    await this.handleWarning()

    if (this.externalSignaturesEnabled) {
      this.chain = await signatureChains.getSignatureChain(this.$route.params.chainId)
    }

    if (this.hasPredefinedSignatureParameters) {
      const { x, y, width, height, page } = this.$route.query
      this.selectedSignatureProfile.savedSignatureFieldParameters = {
        originX: parseInt(x),
        originY: parseInt(y),
        width: parseInt(width),
        height: parseInt(height),
        page: parseInt(page)
      }

      this.$nextTick(() => {
        const visibleSignatureComponent = this.$refs.visibleSignature
        if (visibleSignatureComponent) {
          visibleSignatureComponent.updateSignatureProfile(this.selectedSignatureProfile)
        }
      })
    }
  },

  methods: {
    async handleSignClick() {
      if (this.selectedCredential?.provider) {
        if (!Array.isArray(this.providers) || this.providers.length === 0) {
          try {
            this.providers = await credentialRefs.getExternalProviders('web')
          } catch (e) {}
        }
        if (!this.selectedCredential.idToken || !this.selectedCredential.authMode) {
          const info = await this.getCredentialInfosWithCSC()

          if (!info) return
        }
      }

      const isDirect = !!(
        this.isSealSignMode ||
        this.isImplicitCredential ||
        this.isOAUTH2CODECredential ||
        this.isAlfaTrust ||
        this.selectedCredential?.isEvro ||
        (this.selectedCredential?.provider && !this.isExplicitCredential)
      )

      /* console.log('handleSignClick this.isSealSignMode', this.isSealSignMode)
      console.log('handleSignClick this.isImplicitCredential', this.isImplicitCredential)
      console.log('handleSignClick this.isOAUTH2CODECredential', this.isOAUTH2CODECredential)
      console.log('handleSignClick this.isAlfaTrust', this.isAlfaTrust)
      console.log('handleSignClick this.selectedCredential.isEvro', this.selectedCredential?.isEvro)
      console.log('handleSignClick this.selectedCredential.provider', this.selectedCredential?.provider)

      console.log('handleSignClick this.selectedCredential', this.selectedCredential)
      console.log('isDirect', isDirect) */

      if (isDirect) {
        this.signDoc()
      } else {
        this.showAuthorizationModal()
      }
    },

    getObjectDuplicate(obj) {
      try {
        return JSON.parse(JSON.stringify(obj))
      } catch {
        // console.log('BUG, this.selectedSignatureProfile', this.selectedSignatureProfile, this.credentialsWithCentralKeyToo)
        return null
      }
    },
    async selectedSignatureProfileImplementation(val, oldVal) {
      if (!val) {
        this.selectedCredential = {}
        return
      }

      if (val?.id !== oldVal?.id) {
        if (!val?.savedSignatureFieldParameters) {
          val.savedSignatureFieldParameters = {
            originX: 0,
            originY: 0
          }
        }
      }

      if (this.predefinedSignatureParams) {
        const { originX, originY, width, height, page } = this.$route.query
        val.savedSignatureFieldParameters = {
          originX: parseFloat(originX),
          originY: parseFloat(originY),
          width: width ? parseFloat(width) : undefined,
          height: height ? parseFloat(height) : undefined,
          page: parseInt(page)
        }
      }
      if (val.credentialRefId || val.evroRefId || val.credentialId || val.alfaRefId) {
        if (this.sealEnabled && val.useSeal) {
          if (!this.selectedCredential) {
            this.selectedCredential = {}
          }
          if (val.credentialRefId) {
            this.selectedCredential.id = parseInt(val.credentialRefId)
          } else if (val.evroRefId) {
            this.selectedCredential.id = parseInt(val.evroRefId)
            this.selectedCredential.isEvro = true
          }
          if (val.alfaRefId) {
            this.selectedCredential.id = parseInt(val.alfaRefId)
            this.selectedCredential.isAlfa = true
          }
          // console.log('central key selected', this.selectedCredential)
          this.$nextTick(() => {
            if (this.$refs.form) {
              this.checkFormValidity()
            }
          })
        } else {
          if (val.credentialRefId) {
            this.selectedCredential = this.findCredentialByRefId(
              parseInt(val.credentialRefId)
            )
            if (this.selectedCredential === undefined || !this.selectedCredential) {
              this.selectedCredential = {}
              this.selectedCredential.id = parseInt(val.credentialRefId)
            }
          } else if (val.evroRefId) {
            this.selectedCredential = this.findCredentialByEvroRefId(
              parseInt(val.evroRefId)
            )
            if (this.selectedCredential === undefined || !this.selectedCredential) {
              this.selectedCredential = {}
              this.selectedCredential.id = parseInt(val.evroRefId)
              this.selectedCredential.isEvro = true
            }
          } else if (val.alfaRefId) {
            this.selectedCredential = this.credentialsAlfa.find(
              (cred) => cred.id === val.alfaRefId
            )

            if (this.selectedCredential === undefined || !this.selectedCredential) {
              this.selectedCredential = {}
              this.selectedCredential.id = parseInt(val.alfaRefId)
              this.selectedCredential.isAlfa = true
            }
          } else {
            this.selectedCredential = this.findCredentialById(parseInt(val.credentialId))
            if (this.selectedCredential === undefined || !this.selectedCredential) {
              this.selectedCredential = {}
            }
          }
          if (!this.selectedCredential) {
          }

          this.$nextTick(() => {
            if (this.$refs.form) {
              this.checkFormValidity()
            }
          })
        }
      } else {
        if (!this.sealEnabled) {
          // console.log('no credential id in profile')
        }
        // console.log('no id in profile')
        this.selectedCredential = {}
        if (
          this.useSignatureProfile &&
          this.selectedSignatureProfile.useSeal &&
          this.sealActivated
        ) {
          // this.selectedCredential.id = this.centralKeyId
          this.selectedCredential = this.findCredentialById(-1)
        }
        // this.selectedCredential.id = this.centralKeyId
        this.$refs.form.resetValidation()
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.checkFormValidity()
          }
        })
      }

      const isFabricControlsInited = this.$refs.visibleSignature?.isFabricControlsInited()
      const needToUpdateVisibleSignaturePreview = !isFabricControlsInited || !this.selectedSignatureProfile.sourceImageDataUrl
      if (
        this.useVisibleSignature &&
        this.useSignatureProfile &&
        needToUpdateVisibleSignaturePreview
      ) {
        signatures
          .getVisibleSignature(this.selectedSignatureProfile.id)
          .then((visResp) => {
            // console.log('visResp', visResp)
            this.selectedSignatureProfile.sourceImageDataUrl = visResp.data.imageDataUrl
            // console.log('profile after visResp', this.selectedSignatureProfile)
            this.$nextTick(() => {
              const visibleSignatureComponent = this.$refs.visibleSignature
              visibleSignatureComponent.updateSignatureProfile(
                this.selectedSignatureProfile
              )
            })
          })
      } else {
        // console.log('profile have sourceImageDataUrl, not calling visResp')
      }
    },
    async getCredentialIdFromRef(credentialRefId) {
      const ref = await credentialRefs.getCredentialRef(credentialRefId)
      return ref.credentialId
    },

    async saveImagePosToProfile() {
      // console.debug('Updating signature profile with new position', this.selectedSignatureProfile)
      try {
        const profileNeedToSaveOriginal = this.originalSignatureProfiles.find(
          (obj) => obj.id === this.selectedSignatureProfile.id
        )
        // console.log('this.originalSignatureProfiles', this.originalSignatureProfiles)
        // console.log('profileNeedToSaveOriginal', profileNeedToSaveOriginal)
        const profileNeedToSave = this.getObjectDuplicate(profileNeedToSaveOriginal)

        const visibleSignature = this.$refs.visibleSignature
        const usedSignatureFieldParameters = visibleSignature.getSignatureFieldParameters()

        if (this.useSignatureProfile && usedSignatureFieldParameters) {
          this.$set(profileNeedToSave, 'savedSignatureFieldParameters', {
            ...usedSignatureFieldParameters
          })

          this.$set(this.selectedSignatureProfile, 'savedSignatureFieldParameters', {
            ...usedSignatureFieldParameters
          })
        }

        await signatureProfiles.updateMySignatureProfile(
          profileNeedToSave.id,
          profileNeedToSave
        )
        this.saveImagePositionIcon = 'mdi-content-save-check-outline'
        this.$nextTick(() => {
          this.checkUniquePosition()
        })
      } catch (error) {
        console.error(error)
      }
    },

    async downloadDocument() {
      this.isUploading = true
      try {
        const blob = await documents.downloadDocument(this.$route.params.docId)
        const docuDto = await documents.getDocument(this.$route.params.docId)
        this.document = docuDto
        this.pdfEncrypted =
          docuDto.security === 'ENCRYPTED_WITH_OWNER_PASSWORD' ||
          docuDto.security === 'ENCRYPTED_WITH_USER_PASSWORD'
        const base64 = await this.blobToBase64(blob)
        this.documentBase64 = base64.split(',')[1]
      } catch (error) {
        console.error('downloadDocument error', error)
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    handlePasswordCancelled() {
      showToastError(this.$t('toastMessage.cantDisplayPDFWithoutPasswordSpecified'))
      this.$router.push({ name: 'SignatureChains' })
    },

    onPDFPasswordSpecified(password) {
      this.pdf_password = password
    },

    blobToBase64(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.readAsDataURL(blob)
      })
    },

    selectFirstProfile() {
      if (this.signatureProfiles.length > 0) {
        this.useSignatureProfile = true
        this.selectedSignatureProfile = this.signatureProfiles[0]
        this.selectedCredential = this.getCredentialFromProfile(this.selectedSignatureProfile)
        if (this.signatureProfiles.length === 1) {
          this.useCustomDrawnSignatureImage = false
          this.userDrawedSignatureImage = null
        }
      }
    },
    getCredentialFromProfile(profile) {
      if (!profile) return null

      if (profile.alfaRefId && this.credentialsAlfa) {
        return this.credentialsAlfa.find((c) => c.id === parseInt(profile.alfaRefId))
      }

      if (profile.evroRefId && this.credentialsEvro) {
        return this.credentialsEvro.find((c) => c.id === parseInt(profile.evroRefId))
      }

      if (profile.credentialId && this.activatedCredentials) {
        return this.activatedCredentials.find((c) => c.id === parseInt(profile.credentialId))
      }

      if (profile.credentialRefId && this.credentialRefs) {
        return this.credentialRefs.find((c) => c.id === parseInt(profile.credentialRefId))
      }
      if (profile.useSeal) {
        return {
          credentialId: -1,
          useSeal: true
        }
      } else {
        return null
      }
    },

    selectFirstCredential() {
      if (
        !this.selectedCredential &&
        this.credentialsWithCentralKeyTooComputed.length > 0
      ) {
        this.selectedCredential = this.credentialsWithCentralKeyTooComputed[0]
      }
    },

    async getSignatureProfiles() {
      try {
        this.signatureProfiles = await signatureProfiles.getSignatureProfiles()
        this.originalSignatureProfiles = this.getObjectDuplicate(this.signatureProfiles)

        this.signatureProfiles.forEach((profile) => {
          delete profile.textParams
          // deleting the sourceImageDataUrl here, because we will use the dynamically rendered image later, when profile first selected
          delete profile.sourceImageDataUrl
        })
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async listMyActivatedCredentials() {
      try {
        const resp = await rssp.listMyActivatedCredentials()
        resp.content = resp.content.filter(
          (elem) => elem.keyPurpose === 'ASYMMETRIC_SIGN' && elem.certChain.length > 0
        )

        this.activatedCredentials = resp.content
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async listImportedCredentials() {
      try {
        const pageRequestImported = {
          'page-num': 1,
          'page-size': 50,
          'sort-field': 'user.name',
          'sort-order': 'desc'
        }

        const pageImported = await credentialRefs.listCredentialRefs(
          this.user.id,
          null,
          pageRequestImported
        )

        this.credentialRefs = pageImported.content

        if (this.evroEnabled) {
          const pageEvroImported = await evro.listEvroRefs(
            this.user.id,
            null,
            pageRequestImported
          )
          pageEvroImported.content = pageEvroImported.content.map((item) => {
            return {
              ...item,
              isEvro: true
            }
          })
          this.credentialsEvro = pageEvroImported.content
          // console.log('listImportedCredentials this.credentialsEvro', this.credentialsEvro)
        }

        let pageAlfaImported = { content: [], total: 0, pageNum: 1 }
        if (
          this.$store.state.remoteConfig.alfaEnabled &&
          this.chain?.signingTrailMode !== 'DETACHED'
        ) {
          pageAlfaImported = await alfaRefs.listAlfaRefs(
            this.isAdmin ? null : this.user.id,
            this.search,
            pageRequestImported
          )
          pageAlfaImported.content = (pageAlfaImported.content || []).map((item) => ({
            ...item,
            isAlfa: true
          }))
          this.credentialsAlfa = pageAlfaImported.content
          // console.log('listImportedCredentials this.credentialsAlfa', this.credentialsAlfa)
        }

        this.haveImportedcontent =
          pageImported.content.length > 0 ||
          this.credentialsEvro.length > 0 ||
          this.credentialsAlfa.length > 0
        // console.log('listImportedCredentials this.haveImportedcontent', this.haveImportedcontent)
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async handleWarning() {
      try {
        const resp = await rssp.listMyCredentials()
        resp.content = resp.content.filter(
          (elem) => elem.keyPurpose === 'ASYMMETRIC_SIGN'
        )
        this.myCredentials = resp.content

        const devices = await rssp.listMyDevices()
        this.devicesListTotalItems = devices.total

        const credentialActivatedImplicitTotalItems = this.activatedCredentials.filter(
          (x) => x.authMode === 'IMPLICIT'
        ).length
        const credentialActivatedExplicitTotalItems = this.activatedCredentials.filter(
          (x) => x.authMode === 'EXPLICIT'
        ).length
        const credentialImplicitTotalItems = this.myCredentials.filter(
          (x) => x.authMode === 'IMPLICIT'
        ).length
        this.devicesActiveTotalItems = devices.content.filter(
          (x) => x.enabled === true
        ).length

        this.noDeviceForImplicitCredentialWarningVisible =
          !this.sealActivated &&
          credentialActivatedExplicitTotalItems === 0 &&
          this.devicesListTotalItems === 0 &&
          credentialImplicitTotalItems > 0
        // console.log('handleWarning this.activatedCredentials.length', this.activatedCredentials.length)
        this.noCredentialWarningVisible =
          !this.sealActivated &&
          !this.noDeviceForImplicitCredentialWarningVisible &&
          this.activatedCredentials.length === 0 &&
          !this.haveImportedcontent
        // console.log('handleWarning noCredentialWarningVisible', this.noCredentialWarningVisible)
        this.showBlueWarning =
          this.myCredentials.filter(
            (x) => x.status !== 'ACTIVATED' || x.certChain.length === 0
          ).length > 0 ||
          (credentialActivatedImplicitTotalItems > 0 && this.devicesListTotalItems === 0)
        // console.log('handleWarning showBlueWarning', this.showBlueWarning)
        if (
          credentialActivatedImplicitTotalItems > 0 &&
          this.devicesListTotalItems > 0 &&
          this.devicesActiveTotalItems === 0
        ) {
          this.showBlueWarning = true
        }

        if (
          !this.noDeviceForImplicitCredentialWarningVisible &&
          !!this.noCredentialWarningVisible &&
          this.sealActivated &&
          this.signatureProfiles.length > 0 &&
          this.activatedCredentials.length === 0 &&
          this.credentialRefs.length === 0
        ) {
          this.centralKeyEnabledAndnoProfileWithCentralKeyWarningVisible = !this.signatureProfiles.find(
            (profile) => profile.useSeal === true
          )
        } else {
          this.centralKeyEnabledAndnoProfileWithCentralKeyWarningVisible = false
        }
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    updatedSignatureProfile(variable) {
      // console.log('updatedSignatureProfile', variable)
    },

    updatedTextSize(variable) {
      this.signatureTextSize = variable
    },

    async updatedSignatureProfileImage(variable) {
      this.signatureImg = variable
    },

    pdfLoaded(variable) {
      this.pdfRendered = true
      this.isUploading = false

      if (
        this.sealActivated &&
        this.activatedCredentials.length === 0 &&
        this.credentialRefs.length === 0 &&
        this.credentialsEvro.length === 0 &&
        this.signatureProfiles.length === 1
      ) {
        this.useSignatureProfile = true
        this.selectedSignatureProfile = this.signatureProfiles[0]
        if (!this.selectedCredential) {
          this.selectedCredential = {}
        }
        if (
          this.selectedSignatureProfile.credentialRefId ||
          this.selectedSignatureProfile.evroRefId ||
          this.selectedSignatureProfile.credentialId
        ) {
          this.selectedCredential.id = this.selectedSignatureProfile.credentialRefId
            ? this.selectedSignatureProfile.credentialRefId
            : parseInt(this.selectedSignatureProfile.credentialId)
        }
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.checkFormValidity()
          }
        })

        const visibleSignatureComponent = this.$refs.visibleSignature
        if (!visibleSignatureComponent.isFabricControlsInited()) {
          visibleSignatureComponent.redrawEverything()
        }
      } else {
        /* this.selectFirstProfile()
        this.selectFirstCredential() */

        this.selectedSignatureProfileImplementation(this.selectedSignatureProfile, null)

        this.$nextTick(() => {
          if (this.$refs.form) {
            this.checkFormValidity()
          }
        })
      }
    },

    showAuthorizationModal() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.authorizationModal = true
      this.$nextTick(() => {
        this.$refs.authorizationModalForm.reset()
        this.$nextTick(() => {
          this.$refs.passwordField.focus()
        })
      })
    },

    async showDeclineModal(e) {
      const opts = {
        title: this.$t('dialog.confirmation.title'),
        posX: e.clientX,
        posY: e.clientY
      }
      const conf = await showConfirmationMenu(opts)
      if (!conf) {
        return
      }
      this.declineReasonVisible = true
    },

    checkUniquePosition() {
      if (!this.selectedSignatureProfile?.id) return false
      if (!this.selectedSignatureProfile.savedSignatureFieldParameters) return false
      const p = this.getObjectDuplicate(
        this.selectedSignatureProfile.savedSignatureFieldParameters
      )

      const visibleSignature = this.$refs.visibleSignature
      const actualParams = visibleSignature.getSignatureFieldParameters()

      if (actualParams) {
        const isInDefaultPos =
          p?.originX === actualParams.originX &&
          p?.originY === actualParams.originY &&
          (p?.page ? p?.page : 1) === actualParams.page

        return !isInDefaultPos
      } else {
        return false
      }
    },

    getCurrentProvider() {
      return this.providers.find(
        (provider) => provider.id === this.selectedCredential.provider
      )
    },

    getExternalProviderRDSSBaseURL(providerId) {
      return this.$store.state.remoteConfig.rdssApiUrl + '/v1/extproviders/' + providerId
    },

    handleCSCErrorMessage(error) {
      if (error.message) {
        return this.$t('toastMessage.errorDuringPopupLoginWithDetails', {
          msg: error.message
        })
      } else {
        return this.$t('toastMessage.errorDuringPopupLogin')
      }
    },

    async getCredentialInfosWithCSC() {
      const foundProvider = this.getCurrentProvider()
      const usedOIDC = foundProvider.oidc
      let providerBaseURL

      if (
        usedOIDC.authorizationEndpoint &&
        usedOIDC.tokenEndpoint &&
        usedOIDC.revocationEndpoint
      ) {
        providerBaseURL = this.getExternalProviderRDSSBaseURL(foundProvider.id)
      } else {
        providerBaseURL = foundProvider.apiBaseUrl
      }

      if (!this.selectedCredential.idToken) {
        let oldIdToken = null
        if (foundProvider.id === this.$popupAuth.settings.providerId) {
          const user = await this.$popupAuth.getUser()
          try {
            oldIdToken = user ? await this.$popupAuth.getIdToken() : null
            if (user && !oldIdToken) {
              oldIdToken = await this.$popupAuth.getAccessToken()
            }
          } catch (Err) {
            oldIdToken = null
          }
        }
        if (oldIdToken && foundProvider.id === this.$popupAuth.settings.providerId) {
          this.selectedCredential.idToken = oldIdToken
        } else {
          const settings = {
            authority: usedOIDC.issuer,
            apiBaseUrl: providerBaseURL,
            providerId: foundProvider.id,
            client_id: usedOIDC.clientId,
            response_type: 'code',
            scope: usedOIDC.scope,
            prompt: usedOIDC.prompt,
            monitorSession: false,
            popupWindowFeatures: `location=no,toolbar=no,width=${this.popupLoginWidth},height=${this.popupLoginHeight},left=100,top=100`
          }
          if (usedOIDC.clientSecret) {
            settings.client_secret = usedOIDC.clientSecret
          }

          if (
            usedOIDC.authorizationEndpoint &&
            usedOIDC.tokenEndpoint &&
            usedOIDC.revocationEndpoint
          ) {
            settings.metadata = {
              authorization_endpoint: usedOIDC.authorizationEndpoint,
              // token_endpoint: usedOIDC.tokenEndpoint,
              token_endpoint: providerBaseURL + '/openid/token',
              revocation_endpoint: usedOIDC.revocationEndpoint
            }
          }
          this.$popupAuth.setSettings(settings)
          try {
            const user = await this.$popupAuth.login()
            this.selectedCredential.idToken = user.access_token
            if (!user.id_token) {
              this.selectedCredential.idToken = user.access_token
            } else {
              this.selectedCredential.idToken = user.id_token
            }
          } catch (error) {
            console.error('An error occured during login.', error)
            showToastError(this.handleCSCErrorMessage(error))
            return null
          }
        }
      }

      if (!this.selectedCredential.idToken) {
        showToastError(this.$t('importCredential.loginError'))
        return null
      } else {
        const req = {
          credentialID: this.selectedCredential.credentialId
            ? this.selectedCredential.credentialId
            : this.selectedCredential.id
        }
        try {
          await rssp.joinUserOnRssp()
          const resp = await csc.credentialsInfo(
            providerBaseURL,
            this.selectedCredential.idToken,
            req
          )
          this.$set(this.selectedCredential, 'authMode', resp.authMode.toUpperCase())
          this.$set(this.selectedCredential, 'cert', resp.cert)
          this.$set(this.selectedCredential, 'providerBaseURL', providerBaseURL)
          return resp
        } catch (error) {
          console.error('An error occured during getting credential infos.', error)
          showToastError(this.handleCSCErrorMessage(error))
          return null
        }
      }
    },

    extractDocHashes(jsonArray) {
      return jsonArray.map((item) => item.docHash)
    },

    updateDocHashes(targetObj, docHashes) {
      const chunkSize = Math.ceil(docHashes.length / targetObj.signatures.length)
      for (let i = 0; i < targetObj.signatures.length; i++) {
        const startIdx = i * chunkSize
        targetObj.signatures[i].docHashes = docHashes.slice(
          startIdx,
          startIdx + chunkSize
        )
      }
    },

    async signDoc() {
      if (!this.$refs.form.validate()) {
        return
      }

      const credentialRefId = this.selectedCredential?.provider
        ? this.selectedCredential.id
        : undefined
      const evroRefId = this.selectedCredential?.isEvro
        ? this.selectedCredential.id
        : undefined
      // console.log(credentialRefId)

      if (
        this.selectedCredential &&
        this.selectedCredential.provider &&
        credentialRefId &&
        !this.selectedCredential.idToken
      ) {
        await this.getCredentialInfosWithCSC()
        if (!this.selectedCredential.idToken) {
          console.log('No idToken present, aborting sign')
          return
        }
      }

      const isSealSignMode = this.isSealSignMode

      if (!isSealSignMode) {
        if (this.selectedCredential) {
          let certChainOK = true

          if (this.selectedCredential && this.selectedCredential.id !== -1) {
            if (
              this.selectedCredential.isEvro &&
              (!this.selectedCredential.certificatePem ||
                this.selectedCredential.certificatePem.length === 0)
            ) {
              certChainOK = false
            }
            if (
              !this.selectedCredential.provider &&
              !this.selectedCredential.isEvro &&
              !this.selectedCredential.isAlfa &&
              (!this.selectedCredential.certChain ||
                this.selectedCredential.certChain.length === 0)
            ) {
              certChainOK = false
            }
            if (
              this.selectedCredential.provider &&
              (!this.selectedCredential.cert ||
                this.selectedCredential.cert.certificates.length === 0)
            ) {
              certChainOK = false
            }
          }

          if (
            this.selectedCredential &&
            !certChainOK &&
            !this.isAlfaTrust
          ) {
            showToastError(
              this.$t(
                'documentSignatureCustomization.content.signature.noCertificateError'
              )
            )
            return
          }

          if (this.useSignatureProfile && this.selectedSignatureProfile) {
            const profileCredFoundInActivated = !!this.activatedCredentials.find(
              (cred) => cred.id === parseInt(this.selectedCredential.id)
            )
            const profileCredFoundInImported = !!this.credentialRefs.find(
              (cred) => cred.id === parseInt(this.selectedCredential.id)
            )
            const profileCredFoundInEvro = !!this.credentialsEvro.find(
              (cred) => cred.id === parseInt(this.selectedCredential.id)
            )
            if (
              !profileCredFoundInActivated &&
              !profileCredFoundInImported &&
              !profileCredFoundInEvro &&
              !this.isAlfaTrust
            ) {
              showToastError(this.$t('validation.form.credentialIsDead'))
              return
            }
          }
        }
      }

      const explicitCredential = !isSealSignMode && this.isExplicitCredential
      const implicitCredential = !isSealSignMode && this.isImplicitCredential
      const importedCredential = !isSealSignMode && this.selectedCredential.provider
      this.authorizationModalBtnLoading = explicitCredential
      this.isUploading = isSealSignMode

      /* console.log('isSealSignMode', isSealSignMode)
      console.log('explicitCredential', explicitCredential)
      console.log('this.selectedCredential', this.selectedCredential)
      console.log('importedCredential', importedCredential)
      console.log('implicitCredential', implicitCredential)
      console.log('implicitCredential dynamic cacl', this.selectedCredential && (this.selectedCredential.authMode === CredentialAuthMode.IMPLICIT))
      console.log('this.devicesListTotalItems', this.devicesListTotalItems) */

      if (implicitCredential && !importedCredential && this.devicesListTotalItems === 0) {
        showToastError(
          this.$t(
            'documentSignatureCustomization.content.signature.activeCredentialButNoActivatedDeviceError'
          )
        )
        return
      }

      if (implicitCredential || evroRefId) {
        this.implicitGuid = uuidv4()
      }

      const OAUTH2CODECredential = this.isOAUTH2CODECredential

      try {
        const signRequest = {
          useSeal: isSealSignMode
        }

        if (!isSealSignMode) {
          const credentialOptions = {
            description: this.implicitGuid
          }
          if (credentialRefId) {
            this.$set(credentialOptions, 'credentialRefId', credentialRefId)
            this.$set(
              credentialOptions,
              'authentication',
              this.selectedCredential.idToken
            )
          } else if (evroRefId) {
            this.$set(credentialOptions, 'evroRefId', evroRefId)
          } else if (this.selectedCredential.id) {
            this.$set(credentialOptions, 'credentialId', this.selectedCredential.id)
          }
          if (explicitCredential) {
            this.$set(credentialOptions, 'PIN', this.expAuthPwd.pwd)
          }
          this.$set(signRequest, 'credentialOptions', credentialOptions)
        }

        if (this.pdfEncrypted && this.pdf_password) {
          const documentsOptions = []
          const documentsOption = {
            docId: this.document.id,
            passwordProtection: this.pdf_password
          }
          documentsOptions.push(documentsOption)
          this.$set(signRequest, 'documentsOptions', documentsOptions)
        }

        const signaturesList = []
        const signature = {
          signatureId:
            !this.$route.params.signatureId != null
              ? this.$route.params.signatureId
              : null,
          currentSignatureId:
            !this.$route.params.currentSignatureId != null
              ? this.$route.params.currentSignatureId
              : null,
          comment: this.comment.length > 0 ? this.comment : null
        }

        if (this.useVisibleSignature) {
          if (this.useSignatureProfile && this.selectedSignatureProfile) {
            this.$set(
              signRequest,
              'signatureProfileId',
              this.selectedSignatureProfile.id
            )
          } else if (this.userDrawedSignatureImage) {
            this.$set(signRequest, 'userImageDataUrl', this.userDrawedSignatureImage)
          }
          this.getSignatureFieldParameters()

          const visibleSignature = this.$refs.visibleSignature
          const usedSignatureFieldParameters = visibleSignature.getSignatureFieldParameters()

          if (usedSignatureFieldParameters) {
            this.$set(signature, 'signatureFieldParameters', {
              ...usedSignatureFieldParameters
            })
          }
        }

        signaturesList.push(signature)
        this.$set(signRequest, 'signatures', signaturesList)

        if (this.isAlfaTrust) {
          await this.beginAlfaStart(signaturesList)
          return
        }

        try {
          if (implicitCredential || evroRefId) {
            this.implicitAuthorizationModalKey++
            this.ImplicitAuthorizationModalVisible = true
          }
          let providerAuthorizationTimeoutMilis
          let foundProvider
          if (this.selectedCredential && this.selectedCredential.provider) {
            foundProvider = this.getCurrentProvider()
            providerAuthorizationTimeoutMilis = foundProvider.authorizationTimeoutSecs
              ? foundProvider.authorizationTimeoutSecs * 1000
              : 0
          }
          const usedAuthorizationTimeoutSecs =
            this.selectedCredential && this.selectedCredential.isEvro
              ? this.evroTrustAuthorizationTimeoutMilis
              : this.selectedCredential && this.selectedCredential.provider
                ? providerAuthorizationTimeoutMilis
                : this.authorizationTimeoutMilis
          if (OAUTH2CODECredential) {
            this.$set(signRequest, 'returnHashes', true)
          }

          const sign1Resp = await signatures.sign(
            signRequest,
            implicitCredential || evroRefId ? usedAuthorizationTimeoutSecs : null
          )

          if (OAUTH2CODECredential) {
            let sad
            const docHashes = this.extractDocHashes(sign1Resp)

            if (foundProvider) {
              this.$set(signRequest, 'returnHashes', true)
              const url = window.location.origin
              const settings = {
                authority: foundProvider.oidc.issuer,
                client_id: foundProvider.oidc.clientId,
                popup_redirect_uri: `${url}/popup-callback`,
                response_type: 'code',
                scope: 'credential',
                prompt: foundProvider.oidc.prompt,
                monitorSession: false,
                popupWindowFeatures: {
                  location: false,
                  menubar: false,
                  toolbar: false,
                  height: this.popupLoginHeight,
                  width: this.popupLoginWidth,
                  closePopupWindowAfterInSeconds: -1
                }
              }
              if (foundProvider.oidc.clientSecret) {
                settings.client_secret = foundProvider.oidc.clientSecret
              }
              let providerBaseURL
              if (
                foundProvider.oidc.authorizationEndpoint &&
                foundProvider.oidc.tokenEndpoint &&
                foundProvider.oidc.revocationEndpoint
              ) {
                providerBaseURL = this.getExternalProviderRDSSBaseURL(foundProvider.id)
                settings.metadata = {
                  authorization_endpoint: foundProvider.oidc.authorizationEndpoint,
                  token_endpoint: providerBaseURL + '/openid/token',
                  revocation_endpoint: foundProvider.oidc.revocationEndpoint
                }

                settings.extraQueryParams = {
                  numSignatures: docHashes.length,
                  credentialID: this.selectedCredential.credentialId
                    ? this.selectedCredential.credentialId
                    : this.selectedCredential.id,
                  hash: docHashes,
                  description: 'Please allow RSSP to sign with your key!'
                }
              } else {
                providerBaseURL = foundProvider.apiBaseUrl
              }
              const mgr = new UserManager(settings)
              try {
                const user = await mgr.signinPopup()
                if (user.token_type === 'SAD') {
                  sad = user.access_token
                } else {
                  if (!user.id_token) {
                    this.selectedCredential.idToken = user.access_token
                  } else {
                    this.selectedCredential.idToken = user.id_token
                  }
                  sad = user.sad
                }
              } catch (error) {
                console.error('An error occured during login.', error)
                showToastError(this.handleCSCErrorMessage(error))
                return
              }
            } else {
              const authorizeCredentialRequest = {
                numSignatures: docHashes.length,
                hash: docHashes
              }
              this.$set(
                authorizeCredentialRequest,
                'credentialID',
                this.selectedCredential.credentialId
                  ? this.selectedCredential.credentialId
                  : this.selectedCredential.id
              )
              if (explicitCredential) {
                this.$set(authorizeCredentialRequest, 'PIN', this.expAuthPwd.pwd)
              }

              const authorizeCredentialResp = await csc.authorizeCredential(
                this.selectedCredential.providerBaseURL,
                this.selectedCredential.idToken,
                authorizeCredentialRequest
              )

              sad = authorizeCredentialResp.SAD
            }
            this.$set(signRequest, 'returnHashes', false)
            this.$set(signRequest, 'sad', sad)
            this.updateDocHashes(signRequest, docHashes)

            const sign2Resp = await signatures.sign(
              signRequest,
              implicitCredential ? usedAuthorizationTimeoutSecs : null
            )
          }

          this.back()
          showToastSuccess(this.$t('toastMessage.succesfullySigned'))
        } catch (error) {
          if (error?.response?.data?.code === CommonError.SERVICE_UNAVAILABLE) {
            showToastError(
              this.$t(
                'documentSignatureCustomization.content.signature.toastMessages.sicAborted'
              )
            )
          } else {
            const errorMsg = this.getErrorMessage(error)
            showToastError(errorMsg)
          }
          console.error(error)
        }
      } catch (error2) {
        console.error('signDoc, exception occured', error2)
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      } finally {
        this.authorizationModalBtnLoading = false
        this.isUploading = false
        this.ImplicitAuthorizationModalVisible = false
        this.authorizationModal = false
      }
    },

    back() {
      this.declineReasonVisible = false
      this.$router.push({ name: 'SignatureChains' })
    },

    getSignatureFieldParameters() {
      const visibleSignatureComponent = this.$refs.visibleSignature

      if (visibleSignatureComponent) {
        const signatureImageFieldParameters = visibleSignatureComponent.getSignatureFieldParameters()
        if (signatureImageFieldParameters) {
          this.selectedSignatureProfile.savedSignatureFieldParameters = Object.assign(
            {},
            this.selectedSignatureProfile.savedSignatureFieldParameters,
            signatureImageFieldParameters
          )
        }
      } else {
        console.error('visibleSignature component not found.')
      }
    },

    async getVerificationDetails() {
      try {
        this.userVerificationStatus = await userVerification.getUserVerificationStatus()
        this.userVerified = this.userVerificationStatus.verified
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    checkFormValidity() {
      // Check if the form is valid
      if (this.$refs.form.validate()) {
        this.formValid = true
      } else {
      }
    },
    logValidationErrors() {
      // Check if errorBag exists and is an object
      if (this.$refs.form.errorBag && typeof this.$refs.form.errorBag === 'object') {
        // Iterate through errorBag and log error messages and rules
        for (const fieldName in this.$refs.form.errorBag) {
          const fieldErrors = this.$refs.form.errorBag[fieldName]
          if (Array.isArray(fieldErrors)) {
            fieldErrors.forEach((error) => {
              console.log(
                `Field: ${fieldName}, Error Message: ${error}, Rule: ${error.rule}`
              )
            })
          } else if (typeof fieldErrors === 'string') {
            console.error(`Field: ${fieldName}, Error Message: ${fieldErrors}`)
          } else {
            // Handle if fieldErrors is not an array or string
            console.error(
              `Unexpected errorBag structure for field ${fieldName}:`,
              fieldErrors
            )
          }
        }
      } else {
        console.error('ErrorBag is not properly defined or is not an object')
      }
      const form = this.$refs.form
      if (form.inputs) {
        form.inputs.forEach((input, index) => {
          if (input.hasError || !input.valid) {
            console.log(`Input ${index} have errors:`, {
              id: input._uid,
              label: input.label,
              disabled: input.disabled,
              isDisabled: input.isDisabled, // Sometimes different property
              valid: input.valid,
              hasError: input.hasError,
              errorMessages: input.errorMessages,
              internalErrorMessages: input.internalErrorMessages,
              value: input.internalValue || input.value,
              rules: input.rules
            })
          }
        })
      }
    },
    validateAllFields() {
      // Get all input components and validate them
      const inputs = Object.keys(this.$refs).filter(
        (key) => this.$refs[key] && typeof this.$refs[key].validate === 'function'
      )

      inputs.forEach((inputRef) => {
        this.$refs[inputRef].validate()
      })
    },
    handleFormValidityChange(valid) {
      // valid parameter indicates whether the form is valid or not
      if (!valid) {
        // Additional actions you may want to take when a validation error occurs (use when debugging only)
        // this.logValidationErrors()
      }
    },
    findCredentialByRefId(searchId) {
      const matchingCredential = this.credentialsWithCentralKeyToo.find(
        (item) => item.provider && parseInt(item.id) === searchId
      )
      return { ...matchingCredential }
    },
    findCredentialByEvroRefId(searchId) {
      const matchingCredential = this.credentialsWithCentralKeyToo.find(
        (item) => item.isEvro && parseInt(item.id) === searchId
      )
      return { ...matchingCredential }
    },
    findCredentialById(searchId) {
      const matchingCredential = this.credentialsWithCentralKeyToo.find(
        (item) => !item.provider && !item.isEvro && parseInt(item.id) === searchId
      )
      return { ...matchingCredential }
    },
    getCredentialItemText(item) {
      if (item.provider) {
        return `${item.description} | external: ${item.provider}`
      } else {
        if (item.isEvro) {
          return `${item.description} | evrotrust`
        } else if (item.isAlfa) {
          return `${item.description} | alfasign`
        } else {
          return item.description
        }
      }
    },
    openSignatureModal() {
      this.$refs.signatureDrawer.dialog = true
    },

    async handleCustomSignature(imageData) {
      if (!this.selectedSignatureProfile) {
        this.selectedSignatureProfile = {}
      }
      this.userDrawedSignatureImage = imageData
      this.useCustomDrawnSignatureImage = true

      const req = {
        userImageDataUrl: imageData,
        subsignerName: null
      }
      const mergedBase64 = await signatures.combineImage(req)
      this.selectedSignatureProfile.sourceImageDataUrl = mergedBase64.imageDataUrl

      if (this.predefinedSignatureParams) {
        this.selectedSignatureProfile.savedSignatureFieldParameters = this.predefinedSignatureParams
      }

      this.$nextTick(() => {
        const visibleSignatureComponent = this.$refs.visibleSignature
        visibleSignatureComponent.updateSignatureProfile(this.selectedSignatureProfile)
        /* this.$refs.form.resetValidation()
        this.$refs.selectedSignatureProfile.resetValidation() */
      })
    },
    setSignatureImage(file, imageDataUri, callPreview = true) {
      let updatedDataUri = ''
      const splittedSigImgDataUrl = imageDataUri.split(';')
      updatedDataUri = updatedDataUri.concat(
        splittedSigImgDataUrl[0],
        ';filename=' + file.name + ';',
        splittedSigImgDataUrl[1]
      )
      this.$set(this.signatureProfile, 'sourceImageDataUrl', updatedDataUri)
      if (callPreview) {
        this.initPreview()
      }
    },
    handleCustomSignatureCancelled() {
      this.useSignatureProfile = true
      this.clearCutomSignatureImageClick()
    },

    useCustomSignatureClick() {
      this.useSignatureProfile = false
      this.openSignatureModal()
    },
    useSignatureProfileClick() {
      this.useCustomDrawnSignatureImage = false
      this.useSignatureProfile = true
    },
    clearCutomSignatureImageClick() {
      this.useCustomDrawnSignatureImage = false
      this.userDrawedSignatureImage = null
      if (this.selectedSignatureProfile) {
        this.selectedSignatureProfile.sourceImageDataUrl = undefined
        this.$nextTick(() => {
          this.selectFirstProfile()
        })
      } else {
        this.selectFirstProfile()
      }
    },
    async declineDoc() {
      const signRequest = {
        signatureId:
          !this.$route.params.signatureId != null ? this.$route.params.signatureId : null,
        currentSignatureId:
          !this.$route.params.currentSignatureId != null
            ? this.$route.params.currentSignatureId
            : null,
        comment: this.comment.length > 0 ? this.comment : null
      }
      const signatureId = this.$route.params.signatureId
      try {
        await signatures.decline(signatureId, signRequest)
        showToastSuccess(this.$t('toastMessage.succesfullyDeclined'))
        this.$emit('declined')
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },
    base32Encode(input) {
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'
      const bytes = new TextEncoder().encode(input)
      let bits = ''
      let base32 = ''

      for (const byte of bytes) {
        bits += byte.toString(2).padStart(8, '0')
      }

      for (let i = 0; i < bits.length; i += 5) {
        const chunk = bits.substring(i, i + 5)
        if (chunk.length < 5) {
          base32 += alphabet[parseInt(chunk.padEnd(5, '0'), 2)]
        } else {
          base32 += alphabet[parseInt(chunk, 2)]
        }
      }

      // Optional: pad output to a multiple of 8 characters with '='
      while (base32.length % 8 !== 0) {
        base32 += '='
      }

      return base32
    },
    handleExternalSignatureUploaded(e) {
      if (e === null) e = []
      this.uploadedExternalFile = e[0]
      if (e[0]) {
        this.uploadedExternalFileName = e[0].name.slice(0, -4)

        if (this.chain.type === signatureChainType.UNORDERED) {
          const ids = this.extractIds(this.uploadedExternalFile.name)

          this.externalSignatureIdsCorrect =
            ids.chainId === parseInt(this.chain.id) &&
            ids.currentSignatureId ===
              (this.$route.params.currentSignatureId
                ? parseInt(this.$route.params.currentSignatureId)
                : null)

          if (!this.externalSignatureIdsCorrect) {
            showToastError(
              this.$t('backendErrors.SIGNATURE_CHAIN_CONCURRENT_MODIFICATION')
            )
          }
        } else {
          this.externalSignatureIdsCorrect = true
        }
      } else {
        this.externalSignatureIdsCorrect = false
      }
    },
    extractIds(filename) {
      // Extract the base name without extension
      const base = filename.split('/').pop().split('.')[0]

      // Match the last one or two numeric parts surrounded by underscores
      const match = base.match(/_(\d+)(?:_(\d+))?_?$/)

      if (match) {
        const chainId = parseInt(match[1], 10)
        const currentSignatureId = match[2] ? parseInt(match[2], 10) : null

        return { chainId, currentSignatureId }
      }

      return { chainId: null, currentSignatureId: null }
    },
    async getDownloadFileName(fileName, mode, chainId, currentSignatureId) {
      if (!this.rsspRemoteConf) {
        this.rsspRemoteConf = await rssp.getRemoteConfig()
      }
      const encodedServiceId = this.rsspRemoteConf.serviceId
        ? this.base32Encode(this.rsspRemoteConf.serviceId)
        : ''

      const lastDotIndex = fileName.lastIndexOf('.')

      const hasExtension = lastDotIndex !== -1
      const nameWithoutExt = hasExtension
        ? fileName.substring(0, lastDotIndex)
        : fileName
      const extension = hasExtension ? fileName.substring(lastDotIndex) : ''

      const usedCurrentSignatureId = currentSignatureId || ''

      if (mode === documentDownloadMode.ORIGINAL && extension.toLowerCase() === '.pdf') {
        return (
          fileName +
          '_rdss_' +
          encodedServiceId +
          '_' +
          chainId +
          '_' +
          usedCurrentSignatureId +
          '.' +
          extension
        )
      }
      if (mode === documentDownloadMode.ORIGINAL && extension.toLowerCase() !== '.pdf') {
        return (
          fileName +
          '_rdss_' +
          encodedServiceId +
          '_' +
          chainId +
          '_' +
          usedCurrentSignatureId +
          '.' +
          '.pdf'
        )
      }

      return (
        fileName +
        '_rdss_' +
        encodedServiceId +
        '_' +
        chainId +
        '_' +
        usedCurrentSignatureId +
        (hasExtension ? extension : '.pdf')
      )
    },
    async downloadForExternalSign(e) {
      // getting the latest document

      this.isUploading = true
      try {
        let mode = this.chain.signingTrailMode
        if (!mode || mode === 'NONE') {
          // mode = documentDownloadMode.DETACHED
          mode = documentDownloadMode.ORIGINAL
        }

        if (mode === documentDownloadMode.FULL) {
          showToastError(
            this.$t(
              'documentSignatureCustomization.content.signature.externalSignature.asicNotSupported'
            )
          )
          return
        }

        const blob = await documents.downloadDocument(this.$route.params.docId)
        const docuDto = await documents.getDocument(this.$route.params.docId)

        const fileName = this.chain.origDocument.fileName.replace(/\.[^/.]+$/, '')

        const downloadFileName = await this.getDownloadFileName(
          fileName,
          mode,
          this.chain.id,
          this.$route.params.currentSignatureId != null
            ? this.$route.params.currentSignatureId
            : null
        )

        await FileSaver.saveAs(blob, downloadFileName)
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
      this.isUploading = false
    },
    async uploadExternalSignedDocument(e) {
      let docDto
      try {
        docDto = await documents.uploadDocument(
          this.uploadedExternalFileName,
          this.uploadedExternalFile,
          'EXTERNAL_SIGNATURE'
        )
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
      if (docDto === undefined) {
        return
      }
      const signaturesList = []
      const signature = {
        signatureId:
          !this.$route.params.signatureId != null ? this.$route.params.signatureId : null,
        currentSignatureId:
          !this.$route.params.currentSignatureId != null
            ? this.$route.params.currentSignatureId
            : null,
        externalSignedDocId: docDto.id,
        comment: this.comment.length > 0 ? this.comment : null
      }
      signaturesList.push(signature)
      const signRequest = {
        signatures: signaturesList
      }
      try {
        const signResponse = await signatures.sign(signRequest)
        this.$store.commit('removeCurrentFile')

        this.back()
        showToastSuccess(this.$t('toastMessage.succesfullySubmitted'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async beginAlfaStart(signaturesList) {
      await this.$refs.signAlfasign.open({
        alfaRefId: this.selectedCredential.alfaRefId || this.selectedCredential.id,
        description: this.selectedCredential?.description || 'Alfa credential',
        signatures: signaturesList,
        signatureProfileId:
          this.useVisibleSignature && this.useSignatureProfile
            ? this.selectedSignatureProfile?.id
            : null,
        documentsOptions:
          this.pdfEncrypted && this.pdf_password
            ? [{ docId: this.document.id, passwordProtection: this.pdf_password }]
            : null,
        userImageDataUrl:
          this.useVisibleSignature &&
          !this.useSignatureProfile &&
          this.userDrawedSignatureImage
            ? this.userDrawedSignatureImage
            : undefined
      })
    },

    onAlfaDone() {
      showToastSuccess?.(this.$t('toastMessage.succesfullySigned'))
      this.back()
    },
    onAlfaCancel() {}
  }
}
</script>

<style>
.BlueWarning {
  color: var(--v-accent-base);
}
</style>
